import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import AddCompany from './modelContent/addCompany/AddCompany'
import AddFixedTrip from './modelContent/addFixedTrip/AddFixedTrip'
import AddLocation from './modelContent/addLocation/AddLocation'
import AddUser from './modelContent/addUser/AddUser'
import AddAttribute from './modelContent/addAttribute/AddAttribute'
import BigCarFound from './modelContent/bigCarFound/BigCarFound'
import AddAccount from './modelContent/addAccount/AddAccount'
import ClearOrders from './modelContent/clearOrders/ClearOrders'
import AddVoucher from './modelContent/addVoucher/AddVoucher'
import ReOrder from './modelContent/reOrder/ReOrder'
import AddDiscountCode from './modelContent/addDiscountCode/AddDiscountCode'
import AddAddress from './modelContent/AddAddress/AddAddress'
import BigCarConfirmation from './modelContent/bigCarConfirmation/BigCarConfirmation'
import styles from './modal.module.scss'
import ShowAreaCodes from './modelContent/showAreaCodes/ShowAreaCodes'
import { DispatchContext, StateContext } from 'src/AppContext'
import { IState } from 'src/Interface'
import DeleteConfirmation from 'src/components/deleteConfirmation/DeleteConfirmation'
import { createPortal } from 'react-dom'
import AddAddressPOI from './modelContent/AddAddress/AddAddressPOI'

const modaltypes = {
  addAccount: () => AddAccount,
  addAddress: () => AddAddress,
  addAttribute: () => AddAttribute,
  deleteConfirmation: () => DeleteConfirmation,
  addBranch: () => AddLocation,
  addDiscountCode: () => AddDiscountCode,
  addCompany: () => AddCompany,
  addFixedTrip: () => AddFixedTrip,
  addUser: () => AddUser,
  addVoucher: () => AddVoucher,
  clearOrders: () => ClearOrders,
  reOrder: () => ReOrder,
  bigCarFound: () => BigCarFound,
  showAreaCodes: () => ShowAreaCodes,
  bigCarConfirmation: () => BigCarConfirmation,
  addAddressPOI: () => AddAddressPOI
}

export type ModalTypes = keyof typeof modaltypes

function useOutsideAlerter(ref, show, closeOnClick) {
  const dispatch: React.Dispatch<any> = useContext(DispatchContext)
  const close = useCallback(() => dispatch({ type: 'SHOW_MODAL', showModal: false }), [dispatch])


  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        close()
      }
    }

    // Bind the event listener
    if (show && closeOnClick) {
      document.removeEventListener("mousedown", handleClickOutside);
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [close, closeOnClick, ref, show]);
}

const Modal = React.memo(function DeleteConfirmation() {
  const { modal: { modalType, closeOnClick = true, showModal, showBackDrop, handleClick, ...props }, aside: { show } }: IState = useContext(StateContext)
  const [shouldRender, setRender] = useState(showModal);
  const container = useRef(null)
  const dispatch = useContext(DispatchContext)
  useOutsideAlerter(container, showModal, closeOnClick)
  const close = useCallback(() => dispatch({ type: 'SHOW_MODAL', showModal: false }), [dispatch])


  const closeModal = useCallback((e?: any) => {
    if (e.key === 'Escape') {
      close()
    }
  }, [])


  useEffect(() => {
    if (showModal) {
      window.addEventListener('keydown', closeModal);
    } else {
      window.removeEventListener('keydown', closeModal)
    }
  }, [closeModal, showModal])


  useEffect(() => {
    window.scrollTo(0, 0)
    if (showModal || show) {
      document.querySelector('body').style.overflow = 'hidden'
      document.querySelector('.modal').classList.add("modal-active");
      setRender(true);
    } else {
      document.querySelector('body').style.overflow = 'auto'
      document.querySelector('.modal').classList.remove("modal-active");
    }
  }, [showModal, modalType, show]);

  const ModalComponent: any = useMemo(() => {
    if (modaltypes[modalType]) {
      return modaltypes[modalType]()
    }
  }, [modalType])

  const onAnimationEnd = () => {
    if (!showModal) {
      setRender(false);
      if (!showBackDrop) {
        document.querySelector('.modal').classList.remove("modal-active");
      }
    }
  };


  return createPortal(
    shouldRender && modalType ? (
      showBackDrop ?
        <div className={styles.backDrop}>
          <div ref={container} id='modal-module' onAnimationEnd={onAnimationEnd} className={`${styles.modal} ${showModal ? styles.fadeIn : styles.fadeOut}`}>
            <ModalComponent handleClick={handleClick} close={close} {...props} />
          </div>
        </div> :
        <div ref={container} id='modal-module' onAnimationEnd={onAnimationEnd} className={`${styles.modal} ${showModal ? styles.fadeIn : styles.fadeOut}`}>
          <ModalComponent handleClick={handleClick} close={close} {...props} />
        </div>
    ) : null,
    document.querySelector('.modal'))
})

export default Modal